import Grid from '@mui/material/Grid';
import React, { memo } from 'react';
import { DEFAULT_PAUSE_LEVEL_VALUE } from 'redux/reducer/FlowReducer/constants';
import { FlowNodeContextProvider } from 'pages/Flow/Nodes/FlowNode/FlowNodeContext';
import { alpha, styled } from '@mui/material/styles';
import { flowPalette } from 'pages/Flow/HelpFlow/constants';
import { habuColors } from 'Theme';
import { useSelector } from 'react-redux';


const StyledGrid = styled(Grid, {
  shouldForwardProp: (prop) => ![
    'selected',
    'nodeSize',
    'type',
    'nodeLevel',
    'isFlowCachedMode',
    'selectedStartLevel',
    'selectedPauseLevel',
    'isBranchExcluded',
    'highlightIncluded',
  ].includes(prop),
})(({
  theme,
  nodeSize,
  type,
  selected,
  nodeLevel,
  isFlowCachedMode,
  selectedStartLevel,
  selectedPauseLevel,
  isBranchExcluded,
  highlightIncluded,
}) => {
  // Handle DEFAULT_START_LEVEL_VALUE (which is 'Begin at *') as a special case
  const isDefaultStartLevel = typeof selectedStartLevel === 'string' && selectedStartLevel !== '0';
  const isLevelZeroSelected = selectedStartLevel === 0 || selectedStartLevel === '0';

  // NEW: Treat the default start level ("Begin at *") as -1 so numerical comparisons work correctly
  const effectiveStartLevel = isDefaultStartLevel ? -1 : Number(selectedStartLevel);

  const isLevelDisabled = isFlowCachedMode
    && !isDefaultStartLevel
    && nodeLevel < selectedStartLevel;

  const isPausedOrBeyond = isFlowCachedMode
    && selectedPauseLevel !== DEFAULT_PAUSE_LEVEL_VALUE
    && nodeLevel >= selectedPauseLevel;

  const isExcluded = isBranchExcluded || isLevelDisabled || isPausedOrBeyond;

  // Don't add special border for level 0 when "Begin at *" is selected
  const flowCachedBorder = isFlowCachedMode
    && ((isLevelZeroSelected && nodeLevel === 0) || nodeLevel > effectiveStartLevel)
    && '3px solid';
  const border = flowCachedBorder || (nodeSize === 'MD' ? '1px solid' : '');
  const selectedBackground = nodeSize === 'MD' ? habuColors.neutral[10] : habuColors.neutral[5];
  const cachedNodeToRunBorderColor = isFlowCachedMode
    && ((isLevelZeroSelected && nodeLevel === 0) || nodeLevel > effectiveStartLevel)
    && (selectedPauseLevel > 0 ? nodeLevel < selectedPauseLevel : !Number.isNaN(selectedPauseLevel))
    && theme.palette.primary.light;

  const borderColor = cachedNodeToRunBorderColor || (nodeSize === 'MD' && type ? flowPalette(theme, type).borderColor : '');

  // Subtle glow for cached nodes to run (non-selected) to make highlight more pronounced
  const cachedGlow = (!selected && cachedNodeToRunBorderColor)
    ? `0 0 0 3px ${alpha(cachedNodeToRunBorderColor, 0.28)}`
    : undefined;

  // Unified styling for nodes excluded by branch filtering or level disabling
  const excludedNodeStyles = isExcluded ? {
    opacity: 0.4,
    background: habuColors.neutral[5],
    borderColor: habuColors.neutral[40],
    boxShadow: 'none',
  } : {};

  // Highlight included nodes when branch filtering is active
  const includedNodeStyles = highlightIncluded ? {
    border: '3px solid',
    borderColor: theme.palette.success.main,
    ...(isFlowCachedMode && {
      boxShadow: `0 0 0 3px ${theme.palette.success.active}`,
    }),
  } : {};

  return {
    border,
    borderColor,
    boxShadow: cachedGlow,
    maxWidth: nodeSize === 'MD' ? '400px' : '150px',
    padding: nodeSize === 'MD' ? '' : '8px',
    ...(selected ? {
      borderRadius: isFlowCachedMode ? 5 : 2,
      border: isFlowCachedMode ? '2px solid' : border,
      borderColor: isFlowCachedMode ? theme.palette.primary.main : borderColor,
      background: isFlowCachedMode ? theme.palette.background.paper : selectedBackground,
      boxShadow: '0px 4px 10px 0px rgba(69, 72, 77, 0.12), 0px 1px 2px 0px rgba(59, 66, 77, 0.20)',
      borderTop: nodeSize === 'SM' && type ? `2px solid ${flowPalette(theme, type).borderColor}` : '',
    } : {
      borderRadius: isFlowCachedMode ? 5 : '',
      background: (nodeSize === 'MD' ? theme.palette.background.paper : ''),
    }),
    ...excludedNodeStyles,
    ...includedNodeStyles,
  };
});



export const FlowNode = memo(({
  children,
  selected,
  type,
  customNodeSize,
  nodeLevel,
  isFlowCachedMode,
  selectedStartLevel,
  selectedPauseLevel,
  id,
}) => {
  const canvasNodeSize = useSelector(state => state.flow.nodeSize);
  const nodeSize = customNodeSize || canvasNodeSize;
  const includedNodeIds = useSelector(state => state.flow.includedNodeIds);

  const isFilteringActive = includedNodeIds?.length > 0;
  const isBranchExcluded = isFilteringActive && !includedNodeIds.includes(id);
  const highlightIncluded = isFilteringActive && !isBranchExcluded;

  return (
    <FlowNodeContextProvider value={{ selected, type }}>
      <StyledGrid
        container
        data-testid='flow-node'
        type={type}
        nodeSize={nodeSize}
        selected={selected}
        isFlowCachedMode={isFlowCachedMode}
        selectedStartLevel={selectedStartLevel}
        selectedPauseLevel={selectedPauseLevel}
        nodeLevel={nodeLevel}
        isBranchExcluded={isBranchExcluded}
        highlightIncluded={highlightIncluded}
      >
        {children}
      </StyledGrid>
    </FlowNodeContextProvider>
  );
});
